import { FC, forwardRef, useEffect, useState } from 'react';
import * as Slider from '@radix-ui/react-slider';
import * as Select from '@radix-ui/react-select';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { FaIcon } from '@fe-learner-common';
import { useIntl } from 'react-intl';

type DurationRangeProps = {
  filterKey: string;
  label: string;
  min?: number;
  max?: number;
  onChange?: (range: [number, number]) => void;
  value?: [number, number];
  ariaLabelMinValue: string;
  ariaLabelMaxValue: string;
};

const SelectComp: FC<{
  value: number;
  values: number[];
  onChange: (value: number) => void;
}> = ({ value, values, onChange }) => {
  return (
    <Select.Root
      value={String(value)}
      onValueChange={(val) => onChange(parseInt(val))}
    >
      <Select.Trigger className="l-tab l-tab-md l-tab-tertiary w-full text-body-md text-neutral-500">
        <Select.Value>{value}</Select.Value>
        <FaIcon className="ms-auto" icon={faChevronDown} />
      </Select.Trigger>
      <Select.Content
        position="popper"
        side="bottom"
        className="z-[100] max-h-[200px] min-w-[var(--radix-select-trigger-width)] rounded-xl border bg-neutral-0 shadow-100"
      >
        <Select.Viewport className="flex flex-col gap-y-1 p-2">
          {values.map((value) => (
            <Select.SelectItem
              key={value}
              value={value.toString()}
              className="relative flex items-center rounded-xl bg-white p-2 text-title-sm data-[highlighted]:bg-neutral-100 data-[state=checked]:text-prominence-500  "
            >
              <Select.ItemText>{value}</Select.ItemText>
            </Select.SelectItem>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Root>
  );
};

export const DurationChoice = forwardRef<HTMLDivElement, DurationRangeProps>(
  (
    {
      label,
      filterKey,
      min = 0,
      max = 24,
      onChange,
      value: initialValue,
      ariaLabelMinValue,
      ariaLabelMaxValue,
    },
    ref,
  ) => {
    const intl = useIntl();
    const [value, setValue] = useState<[number, number]>(
      initialValue ?? [min, max],
    );

    const hoursOptions = Array.from({ length: max + 1 }, (_, i) => i);
    const handleSliderChange = (newValue: number[]) => {
      const range = newValue as [number, number];
      setValue(range);
      onChange?.(range);
    };

    useEffect(() => {
      if (initialValue) {
        setValue(initialValue);
      }
    }, [initialValue]);

    const handleDropdownChange = (index: 0 | 1, newValue: number) => {
      const newRange: [number, number] = [...value] as [number, number];
      if (index === 0) {
        newRange[0] = newValue;
        newRange[1] = Math.max(newRange[1], newValue + 1);
      } else {
        newRange[1] = newValue;
        newRange[0] = Math.min(newRange[0], newValue - 1);
      }
      setValue(newRange);
      onChange?.(newRange);
    };
    const ariaKey = `filter-group-${filterKey}`;
    return (
      <div ref={ref} role="group">
        <div className="flex items-center justify-between px-3 py-3.5">
          <h2 className="text-label-md leading-5" id={ariaKey}>
            {label}
          </h2>
        </div>
        <div className="space-y-4 px-3">
          <div className="space-y-2">
            <h3 className="text-label-sm text-neutral-500">
              {intl.formatMessage({
                id: 'learner.common.filter.duration.hours',
              })}
            </h3>
            <div className="flex items-center gap-2">
              <SelectComp
                value={value[0]}
                values={hoursOptions.slice(0, max)}
                onChange={(val) => {
                  handleDropdownChange(0, val);
                }}
              />
              <span>-</span>
              <SelectComp
                value={value[1]}
                values={hoursOptions.slice(Math.max(value[0] + 1, 1))}
                onChange={(val) => {
                  handleDropdownChange(1, val);
                }}
              />
            </div>
          </div>

          <Slider.Root
            className="relative flex w-full touch-none select-none items-center  py-4"
            min={min}
            max={max}
            value={value}
            onValueChange={handleSliderChange}
            step={1}
          >
            <Slider.Track className="relative h-1 w-full grow rounded-full bg-neutral-200">
              <Slider.Range className="absolute h-full rounded-full bg-prominence-500" />
            </Slider.Track>
            <Slider.Thumb
              className="block h-4 w-1 rounded-sm bg-prominence-500 focus:outline-none focus:ring-2 focus:ring-prominence-500 focus:ring-offset-2"
              aria-label={ariaLabelMinValue}
            />
            <Slider.Thumb
              className="block h-4 w-1 rounded-sm bg-prominence-500 focus:outline-none focus:ring-2 focus:ring-prominence-500 focus:ring-offset-2"
              aria-label={ariaLabelMaxValue}
            />
          </Slider.Root>
        </div>
      </div>
    );
  },
);
DurationChoice.displayName = 'DurationChoice';
